/**
 * Central configuration for AI agents and models
 * Used by: ProjectSettings, AnalyzeModal, ImplementModal
 */

// AI Agent providers
export const AI_AGENTS = {
  CURSOR: 'cursor',
  CLAUDE: 'claude',
  CODEX:  'codex',
  GEMINI: 'gemini',
};

// Cursor Agent models — curated subset of `cursor-agent models` CLI
// output. The CLI exposes ~115 entries; surfacing all of them buries
// the useful ones under Low/Medium/High/Max/Thinking/Fast permutations.
// Pattern matches AGENT_OPTIONS in utils/agent-options.js: latest
// flagship per family, plus a couple of speed variants where users
// actually pick the cheap/fast option deliberately.
//
// IDs are passed verbatim as `--model <id>` to the Cursor CLI, so they
// must match the CLI exactly. Re-run `cursor-agent models` and trim
// when Cursor ships a new family — the CLI is the source of truth.
export const CURSOR_MODELS = [
  { id: 'auto',                                name: 'Auto (Recommended)' },

  // Composer (Cursor's in-house, default for new sessions)
  { id: 'composer-2.5-fast',                   name: 'Composer 2.5 Fast (Default)' },
  { id: 'composer-2.5',                        name: 'Composer 2.5' },
  { id: 'composer-2-fast',                     name: 'Composer 2 Fast' },
  { id: 'composer-2',                          name: 'Composer 2' },

  // Anthropic — latest first
  { id: 'claude-opus-4-7-thinking-max',        name: 'Claude Opus 4.7 Max (Thinking)' },
  { id: 'claude-opus-4-7-max',                 name: 'Claude Opus 4.7 Max' },
  { id: 'claude-opus-4-7-thinking-high',       name: 'Claude Opus 4.7 High (Thinking)' },
  { id: 'claude-opus-4-7-high',                name: 'Claude Opus 4.7 High' },
  { id: 'claude-4.6-sonnet-medium-thinking',   name: 'Claude Sonnet 4.6 (Thinking)' },
  { id: 'claude-4.6-sonnet-medium',            name: 'Claude Sonnet 4.6' },
  { id: 'claude-4.6-opus-max-thinking',        name: 'Claude Opus 4.6 Max (Thinking)' },
  { id: 'claude-4.6-opus-max',                 name: 'Claude Opus 4.6 Max' },
  { id: 'claude-4.6-opus-high-thinking',       name: 'Claude Opus 4.6 (Thinking)' },
  { id: 'claude-4.6-opus-high',                name: 'Claude Opus 4.6' },
  { id: 'claude-4.5-opus-high-thinking',       name: 'Claude Opus 4.5 (Thinking)' },
  { id: 'claude-4.5-opus-high',                name: 'Claude Opus 4.5' },
  { id: 'claude-4.5-sonnet-thinking',          name: 'Claude Sonnet 4.5 (Thinking)' },
  { id: 'claude-4.5-sonnet',                   name: 'Claude Sonnet 4.5' },

  // OpenAI — GPT family
  { id: 'gpt-5.5-extra-high',                  name: 'GPT-5.5 Extra High' },
  { id: 'gpt-5.5-high',                        name: 'GPT-5.5 High' },
  { id: 'gpt-5.5-medium',                      name: 'GPT-5.5' },
  { id: 'gpt-5.5-medium-fast',                 name: 'GPT-5.5 Fast' },
  { id: 'gpt-5.4-xhigh',                       name: 'GPT-5.4 Extra High' },
  { id: 'gpt-5.4-high',                        name: 'GPT-5.4 High' },
  { id: 'gpt-5.4-medium',                      name: 'GPT-5.4' },
  { id: 'gpt-5.4-medium-fast',                 name: 'GPT-5.4 Fast' },

  // OpenAI — Codex family (often the better pick for code-heavy work)
  { id: 'gpt-5.3-codex-xhigh',                 name: 'Codex 5.3 Extra High' },
  { id: 'gpt-5.3-codex-high',                  name: 'Codex 5.3 High' },
  { id: 'gpt-5.3-codex',                       name: 'Codex 5.3' },
  { id: 'gpt-5.2-codex-high',                  name: 'Codex 5.2 High' },
  { id: 'gpt-5.2-codex',                       name: 'Codex 5.2' },

  // Google
  { id: 'gemini-3.1-pro',                      name: 'Gemini 3.1 Pro' },
  { id: 'gemini-3.5-flash',                    name: 'Gemini 3.5 Flash' },
  { id: 'gemini-3-flash',                      name: 'Gemini 3 Flash' },

  // xAI / Moonshot
  { id: 'grok-4.3',                            name: 'Grok 4.3' },
  { id: 'kimi-k2.5',                           name: 'Kimi K2.5' },
];

// Claude API models (Anthropic direct). IDs must be valid SDK identifiers
// — short aliases (e.g. 'sonnet-4.6') are normalized to the dated SDK id
// in packages/core/src/constants.js CLAUDE_MODEL_MAP. Latest first.
export const CLAUDE_MODELS = [
  { id: 'claude-opus-4-7',            name: 'Claude Opus 4.7' },
  { id: 'claude-sonnet-4-6',          name: 'Claude Sonnet 4.6 (Recommended)' },
  { id: 'claude-opus-4-6',            name: 'Claude Opus 4.6' },
  { id: 'claude-sonnet-4-5-20250929', name: 'Claude Sonnet 4.5' },
  { id: 'claude-opus-4-5-20251101',   name: 'Claude Opus 4.5' },
  { id: 'claude-haiku-4-5-20251001',  name: 'Claude Haiku 4.5' },
];

// Codex CLI / OpenAI models. Aliases MUST match
// packages/core/src/constants.js CODEX_MODEL_MAP — keep in lockstep.
// Codex CLI passes the model verbatim to OpenAI's API; unmapped values
// pass-through, so adding a new id here works as long as OpenAI accepts
// it. Latest first.
export const CODEX_MODELS = [
  { id: 'auto',           name: 'Auto (o4-mini)' },
  { id: 'gpt-5.5',        name: 'GPT-5.5' },
  { id: 'gpt-5.4',        name: 'GPT-5.4' },
  { id: 'gpt-5.3',        name: 'GPT-5.3' },
  { id: 'gpt-5.3-codex',  name: 'GPT-5.3 Codex' },
  { id: 'gpt-5.2-codex',  name: 'GPT-5.2 Codex' },
  { id: 'gpt-5.2',        name: 'GPT-5.2' },
  { id: 'o4-mini',        name: 'o4-mini' },
  { id: 'o3',             name: 'o3' },
  { id: 'o3-mini',        name: 'o3-mini' },
  { id: 'codex-mini',     name: 'codex-mini' },
  { id: 'gpt-4o',         name: 'GPT-4o' },
  { id: 'gpt-4o-mini',    name: 'GPT-4o mini' },
];

// Gemini CLI / Google Gemini models. Aliases must match
// packages/core/src/constants.js GEMINI_MODEL_MAP — keep in lockstep.
export const GEMINI_MODELS = [
  { id: 'gemini-3-pro',     name: 'Gemini 3 Pro (Recommended)' },
  { id: 'gemini-3-flash',   name: 'Gemini 3 Flash' },
  { id: 'gemini-2.5-pro',   name: 'Gemini 2.5 Pro' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
];

// Get models for a specific agent
export const getModelsForAgent = (agent) => {
  if (agent === AI_AGENTS.CLAUDE) return CLAUDE_MODELS;
  if (agent === AI_AGENTS.CODEX)  return CODEX_MODELS;
  if (agent === AI_AGENTS.GEMINI) return GEMINI_MODELS;
  return CURSOR_MODELS;
};

// Get default model for an agent. Match the runtime defaults in
// packages/core/src/constants.js DEFAULT_MODELS so the UI doesn't lie
// about what will actually run when "Auto"/blank is sent.
export const getDefaultModelForAgent = (agent) => {
  if (agent === AI_AGENTS.CLAUDE) return 'claude-sonnet-4-6';
  if (agent === AI_AGENTS.GEMINI) return 'gemini-3-pro';
  return 'auto';
};
